// src/components/ViewTranscriptor.tsx
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  IconButton,
  Divider,
  Button,
  TextField,
  Collapse,
  Card,
  CardContent,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { TranscriptionData } from "../services/api/type";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { apiService } from "./../services/api/ApiService";
import NotificationBanner from "./common/NotificationBanner";
import LoadingSpinner from "./common/LoadingSpinner";
import { useParams, useNavigate } from "react-router-dom";
import VideoPlayer, { VideoPlayerHandles } from "./VideoPlayer/VideoPlayer";
import { useVideoLoader } from "./VideoPlayer/hooks/useVideoLoader";

interface ViewTranscriptorProps {
  role: string;
}

const MAX_TRANSCRIPTION_LENGTH = 300;

const ViewTranscriptor: React.FC<ViewTranscriptorProps> = ({ role }) => {
  const { id } = useParams<{ id: string }>();
  const [transcriptionData, setTranscriptionData] =
    useState<TranscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFullTranscription, setShowFullTranscription] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSerialization, setShowSerialization] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [videoPosition, setVideoPosition] = useState<
    "top-right" | "bottom-right"
  >("top-right");
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [notification, setNotification] = useState({
    type: "",
    title: "",
    message: "",
    open: false,
  });

  type NotificationType = "error" | "success" | "warning" | "info";

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const videoPlayerRef = useRef<VideoPlayerHandles>(null);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    apiService.setAuthToken(token!);

    const fetchData = async () => {
      try {
        setLoading(true);
        const data: TranscriptionData =
          role === "admin" || role === "Supervisor"
            ? await apiService.getAdminTranscriptionById(id!)
            : await apiService.getTranscriptionById(id!);

        setTranscriptionData(data);
        console.log(data)
        setLoading(false);
      } catch (err) {
        setLoading(false);
        if (err instanceof Error) {
          if (err.message.includes("403") || err.message.includes("Forbidden")) {
            navigate("/");
            return;
          }
          setError(err.message || "Error al cargar los datos");
        } else {
          setError("Error desconocido al cargar los datos");
        }
      }
    };
    

    fetchData();
  }, [id, role, navigate]);

  const videoId = transcriptionData?.videoId
    ? String(transcriptionData.videoId)
    : undefined;
  const { mediaUrl, videoLoading, videoError } = useVideoLoader(videoId);

  const handleDownload = async (id: number) => {
    try {
      const blob =
        role === "admin" || role === "Supervisor"
          ? await apiService.getAdminTranscriptionReportPdfById(id)
          : await apiService.getTranscriptionReportPdfById(id);

      if (!blob || !blob.size) {
        throw new Error("El archivo PDF no está disponible.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const getFormattedDate = () => {
        const date = new Date();
        const formattedDate = date.toISOString().slice(0, 10);
        const formattedTime = date
          .toTimeString()
          .slice(0, 8)
          .replace(/:/g, "-");
        return `document_${formattedDate}_${formattedTime}.pdf`;
      };
      link.download = getFormattedDate();

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setNotification({
        type: "error",
        title: "Error de descarga",
        message:
          "No se pudo descargar el archivo PDF. Por favor, intente de nuevo más tarde.",
        open: true,
      });
    }
  };

  const handleCopy = (
    speaker: string,
    startTime: string,
    finishTime: string,
    text: string
  ) => {
    const textToCopy = `Hablante: ${speaker}\nTiempo: ${startTime} - ${finishTime}\nDetalle: ${text}`;
    navigator.clipboard.writeText(textToCopy);
    setNotification({
      type: "success",
      title: "Copiado",
      message: "El texto ha sido copiado al portapapeles.",
      open: true,
    });
  };

  const handleNavigateToTime = (time: string) => {
    videoPlayerRef.current?.seekTo(time);
  };

  const toggleFullTranscription = () => {
    setShowFullTranscription((prev) => !prev);
  };

  const notificationType: NotificationType =
    notification.type === "error" ||
    notification.type === "success" ||
    notification.type === "warning"
      ? notification.type
      : "warning";

  const filteredSerialization =
    transcriptionData?.serialization.filter(
      (entry: any) =>
        entry.words.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.speaker.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const isTranscriptionLong =
    (transcriptionData?.transcription?.length ?? 0) > MAX_TRANSCRIPTION_LENGTH;
  const displayedTranscription =
    showFullTranscription || !isTranscriptionLong
      ? transcriptionData?.transcription
      : transcriptionData?.transcription.slice(0, MAX_TRANSCRIPTION_LENGTH) +
        "...";

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 300);
      setVideoPosition(window.scrollY > 300 ? "bottom-right" : "top-right");
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!transcriptionData) return null;

  return (
    <Container maxWidth="lg">
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", p: 2 }}
      >
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Transcriptor / Mis Transcripciones / {transcriptionData.name}
        </Typography>

        <Paper elevation={3} sx={{ width: "100%", p: 3, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {transcriptionData.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Realizada el{" "}
                {new Date(transcriptionData.createdAt).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Tooltip title="Descargar PDF">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(transcriptionData.id)}
                >
                  Descargar PDF
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Información importante
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Número de expediente:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {transcriptionData.numberCase}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    ID de Video:
                  </Typography>
                  <Typography variant="body1">
                    {transcriptionData.videoId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Fecha de creación:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(
                      transcriptionData.createdAt
                    ).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    Duración:
                  </Typography>
                  <Typography variant="body1">
                    {transcriptionData.duration}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Temas
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {transcriptionData.topics
                  .split(",")
                  .map((topic: string, index: number) => (
                    <Chip
                      key={index}
                      label={topic.trim().toUpperCase()}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Resumen
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">{transcriptionData.summary}</Typography>
            </Paper>

            <Paper elevation={3} sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  Transcripción
                </Typography>
                <Tooltip title="Copiar Transcripción">
                  <IconButton
                    onClick={() =>
                      handleCopy("", "", "", displayedTranscription!)
                    }
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {displayedTranscription}
              </Typography>
              {isTranscriptionLong && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={toggleFullTranscription}
                  sx={{ mt: 2 }}
                >
                  {showFullTranscription ? "Mostrar menos" : "Mostrar más"}
                  {showFullTranscription ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Button>
              )}
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: "bold", mb: 2 }}
                >
                  Detalles de la transcripcion
                </Typography>
                <IconButton onClick={() => setShowSerialization((v) => !v)}>
                  {showSerialization ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              

              <Collapse in={showSerialization}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Buscar palabras clave"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </Box>
                <Box sx={{ mt: 2 }}>
                  {filteredSerialization.length > 0 ? (
                    filteredSerialization.map((entry: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            Hablante: {entry.speakerTag} - Tiempo:{" "}
                            {entry.startTime} - {entry.finishTime}
                          </Typography>
                          <IconButton
                            onClick={() =>
                              handleCopy(
                                entry.speakerTag,
                                entry.startTime,
                                entry.finishTime,
                                entry.words
                              )
                            }
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                          {entry.words}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleNavigateToTime(entry.startTime)}
                          size="small"
                          sx={{ alignSelf: "flex-start" }}
                        >
                          Ver en Video/Audio
                        </Button>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No se encontraron coincidencias.
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                position: isMobile ? "relative" : "sticky",
                top: isMobile ? 0 : 90,
              }}
            >
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    Video
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <VideoPlayer
                    ref={videoPlayerRef}
                    mediaUrl={mediaUrl}
                    loading={videoLoading}
                    error={videoError}
                    onToast={(msg) =>
                      setNotification({
                        type: "info",
                        title: "Video",
                        message: msg,
                        open: true,
                      })
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      

      {showScrollToTop && (
        <IconButton
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            backgroundColor: theme.palette.background.paper,
            "&:hover": { backgroundColor: theme.palette.action.hover },
          }}
          onClick={handleScrollToTop}
        >
          <ArrowUpwardIcon />
        </IconButton>
      )}

      <NotificationBanner
        type={notificationType}
        title={notification.title}
        message={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
      />
    </Container>
  );
};

export default ViewTranscriptor;
